import { Dropdown } from 'primereact/dropdown';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useApplicantContext } from '@/components/providers/ApplicantContextProvider';
import { useApplicantSubmissionQuery } from '@/components/shared/api/queries/applicant';
import { TabDataState } from '@/components/shared/components/TabDataState';
import { useFeatureFlags } from '@/components/shared/constants/featureFlags';
import { formatDateWithTimezone } from '@/components/shared/utils/utils';

import { ContentWrapper } from '../../Applicant.StyledComponents';
import { Applicant } from '../../Applicant.types';
import { ApplicationCompare, CompareSideProvider } from '../ApplicationCompare';
import { ApplicationView } from '../ApplicationView';
import { AlignedApplicationCompare } from './AlignedApplicationCompare';
import {
    CompareColumns,
    SubmissionPicker,
    SubmissionPickers,
    SubmissionsWrapper,
} from './ApplicantSubmissions.StyledComponents';
import { ApplicantSubmissionSummary } from './ApplicantSubmissions.types';
import {
    getPreviousSubmissionId,
    getSubmittedStatusLabels,
    splitNewestSubmission,
} from './ApplicantSubmissions.utils';
import {
    CURRENT_APPLICATION_LABEL,
    CURRENT_APPLICATION_VALUE,
    LIVE_LOOKUPS_SUFFIX,
    SUBMISSION_TRIGGER_LABELS,
    UNLINKED_SUBMISSION_LABEL,
} from './constants';

const toOptionLabel = (
    { dateCreated, trigger, reviewerEmail }: ApplicantSubmissionSummary,
    submittedStatusLabels: string[],
) =>
    [
        ...(submittedStatusLabels.length > 0 ? submittedStatusLabels : [UNLINKED_SUBMISSION_LABEL]),
        formatDateWithTimezone(dateCreated),
        SUBMISSION_TRIGGER_LABELS[trigger],
        reviewerEmail,
    ]
        .filter(Boolean)
        .join(' · ');

// The live application needs no fetch; a recorded submission is fetched only while it is picked.
const useChosenApplication = (applicantId: string, chosenValue: string | null) => {
    const { applicant } = useApplicantContext();
    const isCurrent = chosenValue === CURRENT_APPLICATION_VALUE;
    const submissionQuery = useApplicantSubmissionQuery({
        applicantId,
        applicantSubmissionId: isCurrent ? null : chosenValue,
    });
    const chosenApplicant: Applicant | undefined = isCurrent ? applicant : submissionQuery.data;

    return {
        chosenApplicant,
        isCurrent,
        isLoading: !isCurrent && submissionQuery.isLoading,
        isLoadingError: !isCurrent && submissionQuery.isLoadingError,
        // refetch reports a failure through isLoadingError, so the rejection needs no handling here.
        retry: () => {
            /* c8 ignore next */ submissionQuery.refetch().catch(() => undefined);
        },
    };
};

interface ApplicantSubmissionsProps {
    // Undefined until the list arrives, and when it fails.
    submissions: ApplicantSubmissionSummary[] | undefined;
}

// The Application tab while submissions are on: the live application or any recorded submission, optionally beside
// another one.
export const ApplicantSubmissions = ({
    submissions: loadedSubmissions,
}: ApplicantSubmissionsProps) => {
    const { applicant, applicantId, applicantProductNames } = useApplicantContext();
    const submissions = loadedSubmissions ?? [];
    const [chosenValue, setChosenValue] = useState(CURRENT_APPLICATION_VALUE);
    // Undefined until the list first arrives and sets the default; a later pick or clear
    // replaces it.
    const [compareValue, setCompareValue] = useState<string | null | undefined>(undefined);
    const [searchParams] = useSearchParams();
    const { isSubmissionsTabCompareEnabled } = useFeatureFlags();
    const linkedSubmissionId = searchParams.get('submissionId');

    const [seenLinkedId, setSeenLinkedId] = useState<string | null>(null);

    // Copied into state, so a followed link replaces an earlier pick and survives the param being cleared.
    if (linkedSubmissionId !== seenLinkedId) {
        setSeenLinkedId(linkedSubmissionId);
        if (linkedSubmissionId) setChosenValue(linkedSubmissionId);
    }

    const { newest, older } = splitNewestSubmission(submissions);
    const isListed = (value: string | null) =>
        value === CURRENT_APPLICATION_VALUE || older.some(({ id }) => id === value);
    // An unknown linked id, or the newest submission's, falls back to the live application.
    const selectedValue = isListed(chosenValue) ? chosenValue : CURRENT_APPLICATION_VALUE;

    // Set once, after any linked submission is picked, so later picks on the left never move it.
    if (compareValue === undefined && loadedSubmissions && linkedSubmissionId === seenLinkedId) {
        setCompareValue(
            isSubmissionsTabCompareEnabled
                ? getPreviousSubmissionId(submissions, selectedValue)
                : null,
        );
    }

    const comparedValue = isListed(compareValue ?? null) ? (compareValue ?? null) : null;

    const left = useChosenApplication(applicantId ?? '', selectedValue);
    const right = useChosenApplication(applicantId ?? '', comparedValue);

    // Highlights wait for both sides, so a side still loading never marks every row of the other as changed.
    const isComparing = Boolean(left.chosenApplicant && comparedValue != null && right.chosenApplicant);

    // Each product's own Submitted count, not a count of submissions, so the label matches the status bar.
    const submissionOptions = older.map((submission) => ({
        label: toOptionLabel(
            submission,
            getSubmittedStatusLabels(applicant, applicantProductNames, submission.id),
        ),
        value: submission.id,
    }));
    // Named after the newest submission, plus what only the live view looks up.
    const options = [
        {
            label: newest
                ? `${toOptionLabel(
                      newest,
                      getSubmittedStatusLabels(applicant, applicantProductNames, newest.id),
                  )} ${LIVE_LOOKUPS_SUFFIX}`
                : CURRENT_APPLICATION_LABEL,
            value: CURRENT_APPLICATION_VALUE,
        },
        ...submissionOptions,
    ];

    return (
        <ContentWrapper data-testid="applicant-submissions">
            <SubmissionsWrapper>
                <SubmissionPickers $isSplit={comparedValue != null}>
                    <SubmissionPicker>
                        <label htmlFor="applicant-submission-select">Submission</label>
                        <Dropdown
                            inputId="applicant-submission-select"
                            data-testid="applicant-submission-select"
                            value={selectedValue}
                            options={options}
                            onChange={(e) => setChosenValue(e.value)}
                            appendTo={document.body}
                        />
                    </SubmissionPicker>
                    {isSubmissionsTabCompareEnabled ? (
                        <SubmissionPicker>
                            <label htmlFor="applicant-submission-compare-select">Compare with</label>
                            <Dropdown
                                inputId="applicant-submission-compare-select"
                                data-testid="applicant-submission-compare-select"
                                value={comparedValue}
                                options={options}
                                onChange={(e) => setCompareValue(e.value ?? null)}
                                placeholder="Choose a submission to compare"
                                showClear
                                appendTo={document.body}
                            />
                        </SubmissionPicker>
                    ) : null}
                </SubmissionPickers>
                <ApplicationCompare isComparing={isComparing}>
                    {isComparing && left.chosenApplicant && right.chosenApplicant ? (
                        <AlignedApplicationCompare
                            left={{
                                applicant: left.chosenApplicant,
                                isSubmission: !left.isCurrent,
                            }}
                            right={{
                                applicant: right.chosenApplicant,
                                isSubmission: !right.isCurrent,
                            }}
                        />
                    ) : (
                        <CompareColumns $isSplit={comparedValue != null}>
                            <CompareSideProvider value="left">
                                <TabDataState
                                    isLoading={left.isLoading}
                                    isLoadingError={left.isLoadingError}
                                    onRetry={left.retry}
                                    errorMessage="Could not load this submission."
                                    testId="applicant-submission"
                                >
                                    {left.chosenApplicant ? (
                                        <ApplicationView
                                            applicant={left.chosenApplicant}
                                            isSubmission={!left.isCurrent}
                                        />
                                    ) : null}
                                </TabDataState>
                            </CompareSideProvider>
                            {comparedValue != null ? (
                                <CompareSideProvider value="right">
                                    <TabDataState
                                        isLoading={right.isLoading}
                                        isLoadingError={right.isLoadingError}
                                        onRetry={right.retry}
                                        errorMessage="Could not load this submission."
                                        testId="applicant-submission-compare"
                                    >
                                        {right.chosenApplicant ? (
                                            <ApplicationView
                                                applicant={right.chosenApplicant}
                                                isSubmission={!right.isCurrent}
                                            />
                                        ) : null}
                                    </TabDataState>
                                </CompareSideProvider>
                            ) : null}
                        </CompareColumns>
                    )}
                </ApplicationCompare>
            </SubmissionsWrapper>
        </ContentWrapper>
    );
};
